"use client";

import { usePathname } from "next/navigation"
import { LogOut } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useState, useEffect } from "react"
import { SlideToConfirm } from "@/components/ui/slide-to-confirm"
import { useLocationTracker } from "@/hooks/use-location-tracker"
import { useWakeLock } from "@/hooks/use-wake-lock"
import { getHasIncompleteTask } from "@/lib/staff_api"
import { get, post } from "@/lib/api"
import { getDeviceId } from "@new-cti/frontend-shared/auth/device-id"
import { attend, leave, getWorkStatus } from "@new-cti/frontend-shared/auth/session-api"
import { useCompany } from "@/contexts/CompanyContext"
import { useAuth } from "@/components/auth-provider"

// パスごとの見出し。未定義のパスは「スタッフマイページ」を表示する
const TITLE_BY_PATH: Record<string, string> = {
  "/messages": "チャット",
  "/logs": "行動ログ",
  "/ng-relationships": "NG関係管理",
  "/message-templates": "メッセージテンプレート",
}

// 全タブ共通のヘッダー。出勤トグルと位置トラッキングをここに集約し、
// 画面遷移してもトグルが消えず・トラッキングが再起動しないようにする。
export function AppHeader() {
  const pathname = usePathname()
  const title = TITLE_BY_PATH[pathname] ?? "スタッフマイページ"

  const { getUserFullName } = useCompany()
  const userName = getUserFullName()
  const { logout } = useAuth()

  // 出勤(位置トラッキングON)状態はサーバで一元管理する（複数端末で食い違わないようにするため）。
  // deviceId 単位で稼働端末を排他し、マウント時にサーバの出勤状態を取得して復元する。
  const sessionApi = { get, post }
  const [deviceId, setDeviceId] = useState("")
  const [isWorking, setIsWorking] = useState(false)
  // 別の端末で稼働中（この端末は稼働端末ではない）ことを示すバナー表示用。
  const [otherDeviceWorking, setOtherDeviceWorking] = useState(false)

  // マウント時: 端末IDを確定し、サーバの出勤状態を取得して初期化する。
  // このアプリを開いた時点で、ユーザーが別端末で稼働中なら稼働をこの端末へ自動で引き継ぐ（後勝ち）。
  useEffect(() => {
    const id = getDeviceId()
    setDeviceId(id)
    let cancelled = false
    ;(async () => {
      let s
      try {
        s = await getWorkStatus(sessionApi, id)
      } catch {
        // 取得失敗時は退勤扱いのまま（安全側）
        return
      }
      if (cancelled || !s) return
      if (s.working && s.isActiveDevice) {
        setIsWorking(true)
      } else if (s.working && !s.isActiveDevice) {
        // 別端末で稼働中 → この端末に稼働を引き継ぐ（後勝ち。旧端末は次の送信で409停止）。
        try {
          await attend(sessionApi, id)
          if (!cancelled) setIsWorking(true)
        } catch {
          if (!cancelled) setOtherDeviceWorking(true)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // sessionApi は安定参照のため依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 出勤: attend で稼働端末をこの端末に確定してからONにする（後勝ち。旧端末は次の送信で自動停止）。
  const startWorking = async () => {
    if (!deviceId) return
    try {
      await attend(sessionApi, deviceId)
      setIsWorking(true)
      setOtherDeviceWorking(false)
    } catch {
      window.alert("出勤処理に失敗しました。通信環境を確認してからもう一度お試しください。")
    }
  }

  // 位置を取得できないまま出勤ONになると「出勤中なのに配車に出ない」勘違いを招くため、
  // 出勤直後に一度も取得できなければ出勤を取り消して許可を促す。
  // attend で立てたサーバ側の出勤状態も leave で取り消す（配車に「?」で残り続けるのを防ぐ）。
  const handleLocationError = () => {
    setIsWorking(false)
    void leave(sessionApi, deviceId).catch(() => {})
    window.alert("位置情報を取得できませんでした。お使いの端末・ブラウザの設定を確認し、位置情報の取得を許可してください。")
  }

  // 別端末が出勤（稼働端末を奪取）した場合、この端末のトラッキングは停止される。
  const handleTakenOver = () => {
    setIsWorking(false)
    setOtherDeviceWorking(true)
  }

  // 出勤中（ON）かつ稼働端末の時のみ位置を定期送信し、画面スリープを防ぐ
  useLocationTracker(isWorking, deviceId, { onError: handleLocationError, onTakenOver: handleTakenOver })
  useWakeLock(isWorking)

  // 退勤時、未完了の配車が残っていれば確認する（キャンセル時はisWorkingのままなのでスライダーは出勤中へ戻る）
  const handleClockOut = async () => {
    try {
      const { hasIncompleteTask } = await getHasIncompleteTask()
      if (hasIncompleteTask && !window.confirm("未完了の配車があります。退勤しますか？")) return
    } catch {
      // 判定失敗時は退勤を妨げない
    }
    try {
      await leave(sessionApi, deviceId)
    } catch {
      // 退勤通知の失敗は致命的ではない（サーバ側はupdated_at鮮度で「?」表示に切り替わる）
    }
    setIsWorking(false)
  }

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="bg-blue-600 text-white p-4 sticky top-0 z-40">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center space-x-3 min-w-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors" aria-label="メニューを開く">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>ログアウト</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="min-w-0">
            <h1 className="text-lg font-bold truncate">{title}</h1>
            <p className="text-sm text-blue-100 truncate">{userName ? `${userName}さん` : ""}</p>
          </div>
        </div>
        {/* 出勤/退勤スライダー（右上）。ONの時のみ位置情報を送信する */}
        <div className="w-52 shrink-0">
          <SlideToConfirm
            onComplete={() => { void startWorking() }}
            onReverse={handleClockOut}
            isCompleted={isWorking}
            label="スライドして出勤"
            completedLabel="出勤中"
            reverseLabel="スライドして退勤"
            colorVariant={isWorking ? "red" : "green"}
            compact
            dataTestId="slide-staff-attendance"
          />
          {/* 別端末で稼働中の場合は注意を表示。出勤スライドでこの端末に切り替えられる。 */}
          {otherDeviceWorking && (
            <p className="mt-1 text-xs text-yellow-200" data-testid="other-device-working">
              別の端末で稼働中です
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
